import { LitElement, css, html, nothing, type PropertyValues } from 'lit';
import { consume } from '@lit/context';
import { customElement, state } from 'lit/decorators.js';
import { inputResponseContext } from '../../contexts/inputResponse';
import {
    AGENT_PORTAL_DASH_URL,
    BackendFormResponse,
    ProducerEmailNotice,
    sendProducerEmailNotice,
} from '../../util/api';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT, getBreakpoint } from '../../util/breakpoints';
import { EVENT, makeCustomEvent } from '../../util/events';
import { PAGE } from '../../util/page';

const emailHeaderTemplate = html`
    <style>
        .email-header-root {
            margin: 0 auto;
            padding: 0;
            width: 100%;
            background: #00465e;
            text-align: center;
        }

        .email-header-inner {
            margin: 0 auto;
            max-width: 640px;
            padding: 24px 16px 24px 32px;
            text-align: left;
        }

        .email-header-logo {
            display: block;
            width: 128px;
            max-width: 100%;
            height: auto;
        }
    </style>
    <div class="email-header-root">
        <div data-type="slot" data-key="xacsrgh4dl" data-label="MAIN NAV - Primary logo"></div>
        <div class="email-header-inner">
            <a href="https://www.bluecrossnc.com">
                <img
                    class="email-header-logo"
                    alt="Blue Cross NC"
                    data-assetid="436326"
                    src="https://image.mail-bcbsnc.com/lib/fe6115707c63057e741c/m/1/88633c02-6050-460e-bda7-9fae22c5f55e.png"
                    width="128"
                />
            </a>
        </div>
    </div>
`;

const emailFooterTemplate = (isMobile: boolean) => html`
    <style>
        .email-footer-root {
            margin: 0 auto;
            width: 100%;
            background: #00465e;
        }

        .email-footer-slot {
            background: #00465e;
        }

        .email-footer-inner {
            margin: 0;
            width: 100%;
        }

        .email-footer-logo-wrap {
            text-align: left;
            padding-top: 32px;
            padding-bottom: 16px;
        }

        .email-footer-copy {
            padding-top: 0;
            padding-bottom: 48px;
            font-family: 'BlueRidgeSans', Aptos, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 22px;
            font-weight: 350;
            color: #ffffff;
            text-align: left;
        }

        .email-footer-logo {
            display: block;
            width: 208px;
            max-width: 100%;
            height: auto;
        }

        .email-footer-text {
            margin: 0;
            color: #ffffff;
        }

        .email-footer-text-spaced {
            margin: 24px 0 0 0;
            color: #ffffff;
        }

        .email-footer-link {
            font-family: 'BlueRidgeSans', Aptos, Helvetica, Arial, sans-serif;
            font-size: 14px;
            line-height: 20px;
            font-weight: 350;
            color: #ffffff;
            text-decoration: underline;
        }
    </style>
    <div class="email-footer-root">
        <div class="email-footer-slot" data-type="slot" data-key="17y69lv24pxj" data-label="Footer content goes here">
            <lh-container gutters=${isMobile ? 'md' : 'xl'} theme="primary-extra-dark" spacing="none">
                <div class="email-footer-inner">
                    <div class="email-footer-logo-wrap">
                        <a href="https://www.bluecrossnc.com">
                            <img
                                class="email-footer-logo"
                                alt="Blue Cross NC"
                                data-assetid="853167"
                                src="https://image.mail-bcbsnc.com/lib/fe6115707c63057e741c/m/1/0cc391ce-b338-41d0-844a-9d7cc6fc95c7.png"
                                width="208"
                            />
                        </a>
                    </div>
                    <div class="email-footer-copy">
                        <p class="email-footer-text">
                            Do not reply to this email. This is an automated system message.
                        </p>
                        <p class="email-footer-text-spaced">
                            This email includes important information about your health plan. If you mark this email as
                            SPAM, you will stop receiving important information about your health plan.
                        </p>
                        <p class="email-footer-text-spaced">
                            This email was sent by:
                            <br />
                            Blue Cross and Blue Shield of North Carolina
                            <br />
                            4615 University Drive, Durham, NC 27707, United States
                        </p>
                        <p class="email-footer-text-spaced">
                            <a
                                class="email-footer-link"
                                href="https://www.bcbsnc.com/policies-best-practices/privacy-policy"
                                target="_blank"
                            >
                                Privacy Policy
                            </a>
                        </p>
                    </div>
                </div>
            </lh-container>
        </div>
        <lh-container theme="primary-dark" gutters="none" spacing="none" content-width="fluid">
            <div
                class="email-footer-operational-slot"
                data-type="slot"
                data-key="operationalunumber"
                data-label="UnumberCPCnumber and date goes here"
                data-allowed-blocks="imageblock; textblock; freeformblock; abtestblock; dynamicblock"
            ></div>
        </lh-container>
    </div>
`;

@customElement('rq-producer-email-entry')
export class ProducerEmailEntryPage extends LitElement {
    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            background: var(--lh-color-brand-dogwood, #f5f7f8);
        }
        .wrapper {
            background: var(--lh-color-brand-dogwood, #f5f7f8);
            border-radius: var(--lh-radii-sm, 8px);
            padding: 24px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
        .email-from-name,
        .email-to-name,
        .email-title-name {
            margin: 4px 0;
        }
        .dashed-box {
            background: var(--lh-color-neutral-white, #ffffff);
            padding: 32px;
            border-radius: var(--lh-radii-sm, 8px);
        }
        .result-error-box {
            color: var(--lh-color-danger-600, #b00020);
            margin-bottom: 8px;
        }
        .meta {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 12px;
        }
        .loader-slot {
            display: inline-flex;
            width: 28px;
            justify-content: center;
            align-items: center;
        }
        .header {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .producer-email-link {
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
        }
        .agent-note-textarea {
            --Form-input-background-color: var(--lh-color-neutral-white, #ffffff);
            --Form-textarea-background-color: var(--lh-color-neutral-white, #ffffff);
        }
        @media (max-width: 767px) {
            .producer-email-link {
                display: block;
            }
        }
    `;

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse!: BackendFormResponse;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @state() private viewportWidth = typeof window === 'undefined' ? 1025 : window.innerWidth;

    private textareaBackgroundRetryId: number | null = null;

    private onResize = () => {
        const width = window.innerWidth;
        if (this.viewportWidth !== width) {
            this.viewportWidth = width;
        }
    };

    connectedCallback(): void {
        super.connectedCallback();
        if (typeof window !== 'undefined') {
            this.viewportWidth = window.innerWidth;
            window.addEventListener('resize', this.onResize);
        }
    }

    disconnectedCallback(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.onResize);
            if (this.textareaBackgroundRetryId !== null) {
                window.cancelAnimationFrame(this.textareaBackgroundRetryId);
                this.textareaBackgroundRetryId = null;
            }
        }
        super.disconnectedCallback();
    }

    @state()
    private notice: ProducerEmailNotice = { ccAgent: false, message: '' };

    @state()
    private loading = false;

    @state()
    private sendDone = false;

    @state()
    private errorHtml = '';

    private get isMobile() {
        const resolvedBreakpoint = this.breakpoint || getBreakpoint(this.viewportWidth);
        return resolvedBreakpoint === BREAKPOINT.PHONE || resolvedBreakpoint === BREAKPOINT.TABLET;
    }

    private get county() {
        return this.inputResponse?.county || 'Durham';
    }

    // private get planYear() {
    //     return this.inputResponse?.effectiveStart?.split('/')?.pop() || '';
    // }

    private get primaryApplicantName() {
        const primary = this.inputResponse?.applicants?.find((a) => a.type?.toLowerCase() === 'primary');
        if (!primary) return '';
        return `${primary.firstName || ''} ${primary.lastName || ''}`.trim();
    }

    private get producerName() {
        const producer = this.inputResponse?.producer;
        if (!producer) return '';
        const fullName = `${producer.firstName || ''} ${producer.lastName || ''}`.trim();
        return fullName || producer.agentName || '';
    }

    private get producerEmail() {
        return this.inputResponse?.producer?.email || '';
    }

    private get producerPhone() {
        return this.inputResponse?.producer?.workPhoneNumber || this.inputResponse?.producer?.phone || '';
    }

    private get producerAgencyName() {
        return this.inputResponse?.producer?.individualAgencyName || this.inputResponse?.producer?.agencyName || '';
    }

    private get primaryApplicantFirstName() {
        const primary = this.inputResponse?.applicants?.find((a) => a.type?.toLowerCase() === 'primary');
        return primary?.firstName || 'Customer';
    }

    private onCancel = () => {
        this.dispatchEvent(
            makeCustomEvent(EVENT.CHANGE_PAGE, {
                page: PAGE.RESULTS,
            })
        );
    };

    private onDashboard = () => {
        // Clear previously saved applicant info so a future New Quote flow starts with a blank form.
        sessionStorage.removeItem('applicantFormData');
        window.location.href = AGENT_PORTAL_DASH_URL();
    };

    private onNewQuote = () => {
        // Clear previously saved applicant info so the New Quote flow starts with a blank form.
        sessionStorage.removeItem('applicantFormData');
    };

    private onMessageInput = (e: Event) => {
        const target = e.target as HTMLTextAreaElement;
        this.notice = { ...this.notice, message: target.value || '' };
    };

    private onCcAgentToggle = (e: Event) => {
        const target = e.target as HTMLInputElement;
        this.notice = { ...this.notice, ccAgent: !!target.checked };
    };

    private applyAgentNoteTextareaBackground(): void {
        const textareaHost = this.renderRoot.querySelector('lh-textarea.agent-note-textarea') as
            | (HTMLElement & { shadowRoot: ShadowRoot | null })
            | null;

        if (!textareaHost?.shadowRoot) {
            return;
        }

        const shadow = textareaHost.shadowRoot;
        const control = shadow.querySelector('.lh-textarea__control') as HTMLElement | null;
        const nativeTextarea = shadow.querySelector('textarea') as HTMLTextAreaElement | null;

        if (control) {
            control.style.setProperty('background-color', '#ffffff', 'important');
            control.style.setProperty('background', '#ffffff', 'important');
        }

        if (nativeTextarea) {
            nativeTextarea.style.setProperty('background-color', '#ffffff', 'important');
            nativeTextarea.style.setProperty('background', '#ffffff', 'important');
        }
    }

    private scheduleAgentNoteTextareaBackgroundApply(attempt = 0): void {
        if (typeof window === 'undefined') {
            return;
        }

        if (attempt === 0 && this.textareaBackgroundRetryId !== null) {
            window.cancelAnimationFrame(this.textareaBackgroundRetryId);
            this.textareaBackgroundRetryId = null;
        }

        this.applyAgentNoteTextareaBackground();

        if (attempt >= 5) {
            this.textareaBackgroundRetryId = null;
            return;
        }

        this.textareaBackgroundRetryId = window.requestAnimationFrame(() => {
            this.scheduleAgentNoteTextareaBackgroundApply(attempt + 1);
        });
    }

    protected firstUpdated(changedProperties: PropertyValues<this>): void {
        super.firstUpdated(changedProperties);
        this.scheduleAgentNoteTextareaBackgroundApply();
    }

    protected updated(changedProperties: PropertyValues<this>): void {
        super.updated(changedProperties);
        this.scheduleAgentNoteTextareaBackgroundApply();
    }

    private async onSend() {
        this.errorHtml = '';
        this.loading = true;

        try {
            const result = await sendProducerEmailNotice(this.inputResponse, this.notice, this.county);

            if (!result.success) {
                this.errorHtml = result.errorMessage || 'There was a problem sending your email. Please try again.';
                this.sendDone = false;
            } else {
                this.sendDone = true;
            }
        } finally {
            this.loading = false;
        }
    }

    private renderSent() {
        return html`
            <lh-container
                theme="neutral-light"
                content-width="sm"
                gutters="none"
                spacing="tight"
                padding-top="xl"
                padding-bottom="xl"
            >
                <lh-container gutters="none" theme="neutral-light" alignment="center">
                    <lh-typography variant="h6">Your e-mail notice was sent successfully.</lh-typography>
                    <lh-button-group alignment="center" margin-top="lg">
                        <!-- Temporarily disabled NEW QUOTE page navigation -->
                        <lh-button
                            variant="filled"
                            href="${import.meta.env
                                .VITE_AWS_URL}/get-a-quote/applicant-information?psale=y&amp;productTypeName=BlueAdvantage"
                            @click=${this.onNewQuote}
                        >
                            NEW QUOTE
                        </lh-button>
                        <lh-button
                            variant="filled"
                            @click=${this.onDashboard}
                            analytics-id="rate-quote:producer-email-entry:dashboard-button:button-text"
                            >DASHBOARD</lh-button
                        >
                    </lh-button-group>
                </lh-container>
            </lh-container>
        `;
    }

    private renderForm() {
        return html`
            <lh-container
                theme="neutral-light"
                content-width="lg"
                gutters="none"
                spacing="tight"
                context-id="producer-email-entry-form"
            >
                <lh-container gutters="lg" theme="neutral-light" margin-bottom="lg">
                    <div class="result-error-box" .innerHTML=${this.errorHtml}></div>
                    <lh-typography type="heading" size=${this.isMobile ? 'xl' : 'xl'}>Rate quote notice</lh-typography>
                    <lh-typography color="subtle">
                        The notification displayed below will be e-mailed to your customer to inform them a rate quote
                        is available for review. You can enter optional comments in the text box provided.
                        <strong>The "View your quote" button will be enabled for the customer.</strong>
                    </lh-typography>
                    <lh-typography>
                        <lh-checkbox
                            name="copy-of-quote"
                            value="agent"
                            ?default-checked=${this.notice.ccAgent}
                            @lh-change=${this.onCcAgentToggle}
                        >
                            <lh-typography color="subtle"
                                >Check this box if you'd like to send a copy of this quote to yourself.</lh-typography
                            >
                        </lh-checkbox>
                    </lh-typography>
                    <lh-typography color="subtle">
                        Your e-mail address has been included in the e-mail to remind your customer to contact you
                        directly. If your customer replies to the system generated e-mail, the reply will not reach you.
                        <strong>
                            Selecting the Cancel button will delete the e-mail and a draft will not be saved.
                        </strong>
                    </lh-typography>
                </lh-container>

                <div class="dashed-box">
                    <lh-typography class="email-from-name" color="subtle">From: ${this.producerName}</lh-typography>
                    <lh-typography class="email-to-name" color="subtle"
                        >To: ${this.primaryApplicantName || 'Client'}</lh-typography
                    >
                    <lh-typography color="subtle">Subject: Rate quote for Blue Cross NC plan</lh-typography>
                    <lh-divider spacing-top="md"></lh-divider>
                    <lh-container gutters="none" margin-top="md" theme="transparent">
                        ${emailHeaderTemplate}
                        <lh-container
                            gutters=${this.isMobile ? 'md' : 'xl'}
                            margin-top="md"
                            margin-bottom="xl"
                            theme="transparent"
                        >
                            <lh-typography spacing-top="md" type="heading" size=${this.isMobile ? 'xl' : 'xl'}>
                                Your Blue Cross NC rate quote
                            </lh-typography>
                            <lh-typography class="email-title-name" spacing-top="md" color="subtle"
                                >Hello ${this.primaryApplicantFirstName},</lh-typography
                            >
                            <lh-typography spacing-top="md" color="subtle">
                                Your Blue Cross and Blue Shield of North Carolina rate quote is ready for you to review.
                            </lh-typography>
                            <lh-typography spacing-top="md" color="subtle">
                                If you have any questions about the rate quote or plan benefits, contact me at
                                ${this.producerEmail}.
                                <strong>Don't reply directly to this email, as I won't receive the reply.</strong>
                            </lh-typography>
                            <lh-button variant="filled" size="lg" spacing-top="md">View your quote</lh-button>
                            <lh-typography spacing-top="md" size="lg" type="heading"
                                >When you're ready to apply</lh-typography
                            >
                            <lh-typography spacing-top="md" color="subtle">
                                You can set up a user ID and password to save your application progress and return
                                anytime.
                            </lh-typography>
                            <lh-typography spacing-top="md" size="lg" type="heading">
                                Additional information from your agent
                            </lh-typography>
                            <lh-typography spacing-top="md" color="subtle" size="sm">
                                <i>Add any additional comments to the customer here.</i>
                            </lh-typography>
                            <lh-textarea
                                class="agent-note-textarea"
                                name="description"
                                min-count="5"
                                rows="5"
                                help-text="Minimum of 5 characters"
                                value=${this.notice.message}
                                @lh-input=${this.onMessageInput}
                                character-count="1000"
                            >
                            </lh-textarea>
                            <lh-typography spacing-top="md" color="subtle"
                                >Thank you for considering Blue Cross NC for your health care coverage.</lh-typography
                            >
                            <lh-typography class="agent-business-card" spacing-top="md" color="subtle">
                                Thanks,<br />
                                ${this.producerName ? html`${this.producerName}<br />` : nothing}
                                ${this.producerAgencyName ? html`${this.producerAgencyName}<br />` : nothing}
                                ${this.producerPhone ? html`${this.producerPhone}<br />` : nothing}
                                ${this.producerEmail
                                    ? html`<a href="mailto:${this.producerEmail}" class="producer-email-link"
                                              >${this.producerEmail}</a
                                          ><br />`
                                    : nothing}
                            </lh-typography>
                        </lh-container>
                    </lh-container>
                    ${emailFooterTemplate(this.isMobile)}
                    <lh-container
                        gutters="none"
                        margin-top="lg"
                        content-width="fluid"
                        alignment="center"
                        spacing="none"
                    >
                        <div class="meta">
                            <div>
                                <lh-button-group orientation="horizontal" size="lg" alignment="right">
                                    <lh-button variant="outlined" @click=${this.onCancel}>Cancel</lh-button>
                                    <lh-button variant="filled" ?disabled=${this.loading} @click=${this.onSend}>
                                        Send
                                    </lh-button>
                                </lh-button-group>
                                <span class="loader-slot">
                                    ${this.loading ? html`<lh-loader></lh-loader>` : nothing}
                                </span>
                            </div>
                        </div>
                    </lh-container>
                </div>
            </lh-container>
        `;
    }

    render() {
        return html`${this.sendDone ? this.renderSent() : this.renderForm()}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-producer-email-entry': ProducerEmailEntryPage;
    }
}
